import React, { useState, useEffect } from 'react';
import { Link as LinkIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface LinkPreviewProps {
  url: string;
  isGlobalDarkMode: boolean;
}

interface PreviewData {
  title?: string;
  description?: string;
  image?: string;
  url: string;
  hostname: string;
  isYoutube?: boolean;
}

const getYoutubeVideoId = (url: string) => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};

const globalCache = new Map<string, PreviewData | { error: true }>();
const inFlightRequests = new Map<string, Promise<any>>();

const LinkPreview: React.FC<LinkPreviewProps> = ({ url, isGlobalDarkMode }) => {
  const [data, setData] = useState<PreviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let isMounted = true;
    
    const fetchMetadata = async () => {
      try {
        setLoading(true);
        const youtubeId = getYoutubeVideoId(url);
        
        // Fast path for YouTube (skip API to avoid 400/429)
        if (youtubeId) {
          const ytData: PreviewData = {
            title: 'YouTube Video',
            image: `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`,
            url: url,
            hostname: 'youtube.com',
            isYoutube: true
          };
          globalCache.set(url, ytData);
          if (isMounted) {
            setData(ytData);
            setLoading(false);
          }
          return;
        }

        // Cache lookup
        if (globalCache.has(url)) {
          const cached = globalCache.get(url)!;
          if (isMounted) {
            if ('error' in cached) setError(true);
            else setData(cached as PreviewData);
            setLoading(false);
          }
          return;
        }

        // Deduplicate concurrent requests
        let requestPromise = inFlightRequests.get(url);
        if (!requestPromise) {
          requestPromise = fetch(`https://api.microlink.io?url=${encodeURIComponent(url)}`).then(res => res.json());
          inFlightRequests.set(url, requestPromise);
        }

        const result = await requestPromise;
        
        if (result.status === 'success') {
          const previewData: PreviewData = {
            title: result.data.title || '',
            description: result.data.description || '',
            image: result.data.image?.url || result.data.logo?.url || '',
            url: result.data.url,
            hostname: new URL(url).hostname
          };
          globalCache.set(url, previewData);
          if (isMounted) setData(previewData);
        } else {
          globalCache.set(url, { error: true });
          if (isMounted) setError(true);
        }
      } catch (err) {
        globalCache.set(url, { error: true });
        if (isMounted) setError(true);
      } finally {
        inFlightRequests.delete(url);
        if (isMounted) setLoading(false);
      }
    };

    fetchMetadata();
    
    return () => {
      isMounted = false;
    };
  }, [url]);

  if (loading) {
    return (
      <div className={cn(
        "mt-2 w-full max-w-[400px] h-[80px] rounded-lg border animate-pulse",
        isGlobalDarkMode ? "bg-card/50 border-border" : "bg-gray-100 border-gray-200"
      )} />
    );
  }

  if (error || !data || (!data.title && !data.image)) {
    // Fallback simple link card if no rich metadata
    return (
      <a 
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          "mt-2 flex items-center gap-3 p-3 rounded-lg border transition-colors max-w-[400px]",
          isGlobalDarkMode ? "bg-card border-border hover:bg-accent" : "bg-gray-50 border-gray-200 hover:bg-gray-100"
        )}
      >
        <div className={cn("p-2 rounded-full", isGlobalDarkMode ? "bg-background" : "bg-white")}>
          <LinkIcon className="w-4 h-4 text-gray-500" />
        </div>
        <div className="flex flex-col overflow-hidden min-w-0">
          <span className={cn("text-sm font-medium truncate", isGlobalDarkMode ? "text-foreground" : "text-gray-900")}>
            {new URL(url).hostname}
          </span>
          <span className="text-[10px] text-blue-500 truncate">{url}</span>
        </div>
      </a>
    );
  }

  return (
    <a 
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "mt-2 flex flex-col sm:flex-row overflow-hidden rounded-lg border transition-all max-w-[450px] group",
        isGlobalDarkMode ? "bg-card border-border hover:bg-accent/50 hover:border-gray-600" : "bg-white border-gray-200 hover:bg-gray-50 hover:shadow-sm"
      )}
    >
      {data.image && (
        <div className={cn(
          "shrink-0 bg-gray-100 relative overflow-hidden",
          data.isYoutube ? "w-full sm:w-[200px] h-[150px] sm:h-auto border-b sm:border-b-0 sm:border-r border-border/50" : "sm:w-[120px] h-[120px] sm:h-auto border-b sm:border-b-0 sm:border-r border-border/50"
        )}>
          <img 
            src={data.image} 
            alt={data.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            onError={(e) => {
              if (data.isYoutube && (e.target as HTMLImageElement).src.includes('maxresdefault')) {
                 // Fallback to hqdefault if maxres doesn't exist
                 (e.target as HTMLImageElement).src = `https://img.youtube.com/vi/${getYoutubeVideoId(url)}/hqdefault.jpg`;
              } else {
                 (e.target as HTMLElement).style.display = 'none';
              }
            }}
          />
        </div>
      )}
      <div className="flex flex-col flex-1 p-3 min-w-0 justify-center">
        <span className={cn("text-[10px] font-semibold mb-1 uppercase tracking-wider line-clamp-1", isGlobalDarkMode ? "text-gray-400" : "text-gray-500")}>
          {data.hostname}
        </span>
        <h4 className={cn("text-sm font-bold line-clamp-2 mb-1", isGlobalDarkMode ? "text-foreground" : "text-gray-900")}>
          {data.title || data.hostname}
        </h4>
        {data.description && (
          <p className={cn("text-[11px] line-clamp-2 leading-relaxed", isGlobalDarkMode ? "text-gray-400" : "text-gray-600")}>
            {data.description}
          </p>
        )}
      </div>
    </a>
  );
};

export default LinkPreview;
